import "./Bank.css";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const Bank = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    serviceableAreas: [],
    bankAccount: "",
    ifscCode: "",
    upiId: ""
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(3);
  const [selectedArea, setSelectedArea] = useState("");

  // Add body class on mount, remove on unmount
  useEffect(() => {
    document.body.classList.add('bank-active');
    return () => {
      document.body.classList.remove('bank-active');
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Serviceable areas validation
    if (formData.serviceableAreas.length === 0) {
      newErrors.serviceableAreas = "Please add at least one serviceable area";
    }

    // Bank account validation
    if (!formData.bankAccount.trim()) {
      newErrors.bankAccount = "Bank account number is required";
    } else if (!/^\d{9,18}$/.test(formData.bankAccount.replace(/\s/g, ''))) {
      newErrors.bankAccount = "Please enter a valid bank account number (9-18 digits)";
    }

    // IFSC code validation
    if (!formData.ifscCode.trim()) {
      newErrors.ifscCode = "IFSC code is required";
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.toUpperCase())) {
      newErrors.ifscCode = "Please enter a valid IFSC code (e.g., SBIN0000123)";
    }

    // UPI ID validation
    if (!formData.upiId.trim()) {
      newErrors.upiId = "UPI ID is required";
    } else if (!/^[\w\.-]+@[\w\.-]+$/.test(formData.upiId)) {
      newErrors.upiId = "Please enter a valid UPI ID (e.g., user@paytm)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Store form data in localStorage for demo purposes
      localStorage.setItem('bankDetails', JSON.stringify(formData));
      localStorage.setItem('signupStep', '3');
      localStorage.setItem('registrationComplete', 'true');
      
      console.log('Bank details saved:', formData);
      console.log('Registration completed successfully!');
      
      // Navigate to dashboard
      navigate("/Dashboard");
      
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrors({ submit: "Error submitting form. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const formatBankAccount = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Add spaces every 4 digits for readability
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const handleBankAccountChange = (e) => {
    const formatted = formatBankAccount(e.target.value);
    setFormData({ ...formData, bankAccount: formatted });
    
    if (errors.bankAccount) {
      setErrors(prev => ({ ...prev, bankAccount: '' }));
    }
  };

  const formatIFSC = (value) => {
    // Remove all non-alphanumeric characters and convert to uppercase
    return value.replace(/[^A-Z0-9]/g, '').toUpperCase();
  };

  const handleIFSCChange = (e) => {
    const formatted = formatIFSC(e.target.value);
    if (formatted.length <= 11) {
      setFormData({ ...formData, ifscCode: formatted });
      
      if (errors.ifscCode) {
        setErrors(prev => ({ ...prev, ifscCode: '' }));
      }
    }
  };

  const serviceableAreas = [
    "Mumbai Central",
    "Mumbai South",
    "Mumbai North",
    "Pune",
    "Nagpur",
    "Nashik",
    "Aurangabad",
    "Kolhapur",
    "Solapur",
    "Sangli"
  ];

  const handleAddArea = () => {
    if (selectedArea && !formData.serviceableAreas.includes(selectedArea)) {
      setFormData({
        ...formData,
        serviceableAreas: [...formData.serviceableAreas, selectedArea]
      });
      setSelectedArea("");
      
      if (errors.serviceableAreas) {
        setErrors(prev => ({ ...prev, serviceableAreas: '' }));
      }
    }
  };

  const handleRemoveArea = (areaToRemove) => {
    setFormData({
      ...formData,
      serviceableAreas: formData.serviceableAreas.filter(area => area !== areaToRemove)
    });
  };

  return (
    <div className="bank-page-body">
      <link
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css"
        rel="stylesheet"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
      
      <div className="bank-container">
        <button 
          className="bank-back-button" 
          onClick={() => navigate("/Studio")}
        >
          <i className="fas fa-arrow-left"></i> Back to Studio
        </button>
        
        

        <div className="bank-left">
          <img 
            alt="Professional illustration of a photographer with banking and payment symbols" 
            height="400" 
            src="/images/bank.png" 
            width="400"
          />
        </div>
        
        <div className="bank-right">
          <a className="bank-back" href="#" onClick={(e) => { 
            e.preventDefault(); 
            navigate("/Studio"); 
          }}>
            <i className="fas fa-arrow-left"></i>
            Back
          </a>
          
          <h2>Step 3 of 3 - Final Details</h2>

          {/* Progress Indicator */}
          <div className="bank-progress">
            <div className="bank-progress-dot completed"></div>
            <div className="bank-progress-dot completed"></div>
            <div className="bank-progress-dot active"></div>
          </div>

          {errors.submit && (
            <div style={{ 
              color: "#e53935", 
              background: "rgba(229, 57, 53, 0.1)", 
              padding: "0.75rem", 
              borderRadius: "8px", 
              marginBottom: "1rem",
              fontSize: "0.875rem",
              fontWeight: "500"
            }}>
              {errors.submit}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <h3 style={{ 
              color: '#333', 
              fontSize: '1.125rem', 
              fontWeight: '600', 
              marginBottom: '1rem' 
            }}>
              Serviceable Areas
            </h3>
            
            <div className="bank-form-group">
              <label htmlFor="serviceable-areas">Select Areas *</label>
              <select 
                id="serviceable-areas"
                value={selectedArea}
                onChange={(e) => setSelectedArea(e.target.value)}
              >
                <option value="">Select area</option>
                {serviceableAreas.map((area, index) => (
                  <option key={index} value={area}>{area}</option>
                ))}
              </select>
            </div>
            
            <div className="bank-form-group">
              <button 
                type="button"
                className="bank-add-btn"
                onClick={handleAddArea}
                disabled={!selectedArea}
              >
                <i className="fas fa-plus" style={{ marginRight: '0.5rem' }}></i>
                Add Area
              </button>
            </div>

            {/* Selected Areas Display */}
            {formData.serviceableAreas.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#333', marginBottom: '0.5rem', display: 'block' }}>
                  Selected Areas:
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {formData.serviceableAreas.map((area, index) => (
                    <span 
                      key={index}
                      style={{
                        background: 'linear-gradient(135deg, #ff4d4d 0%, #ff6b6b 100%)',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      {area}
                      <button
                        type="button"
                        onClick={() => handleRemoveArea(area)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {errors.serviceableAreas && (
              <span style={{ color: "#e53935", fontSize: "0.75rem", marginBottom: "1rem", display: "block" }}>
                {errors.serviceableAreas}
              </span>
            )}
            
            <h3 style={{ 
              color: '#333', 
              fontSize: '1.125rem', 
              fontWeight: '600', 
              margin: '2rem 0 1rem',
              borderTop: '1px solid rgba(230, 230, 230, 0.8)',
              paddingTop: '1.5rem'
            }}>
              Bank Details
            </h3>
            
            <div className="bank-form-group">
              <label htmlFor="bank-account">Bank Account Number *</label>
              <input 
                id="bank-account" 
                name="bankAccount"
                placeholder="1234 5678 9012 3456" 
                type="text"
                className="bank-input"
                value={formData.bankAccount}
                onChange={handleBankAccountChange}
                maxLength="23" // 18 digits + 4 spaces
                required
              />
              {errors.bankAccount && (
                <span style={{ color: "#e53935", fontSize: "0.75rem", marginTop: "0.25rem", display: "block" }}>
                  {errors.bankAccount}
                </span>
              )}
            </div>
            
            <div className="bank-form-group">
              <label htmlFor="ifsc-code">IFSC Code *</label>
              <input 
                id="ifsc-code" 
                name="ifscCode"
                placeholder="SBIN0000123" 
                type="text"
                className="bank-input"
                value={formData.ifscCode}
                onChange={handleIFSCChange}
                maxLength="11"
                required
              />
              {errors.ifscCode && (
                <span style={{ color: "#e53935", fontSize: "0.75rem", marginTop: "0.25rem", display: "block" }}>
                  {errors.ifscCode}
                </span>
              )}
            </div>
            
            <div className="bank-form-group">
              <label htmlFor="upi-id">UPI ID *</label>
              <input 
                id="upi-id" 
                name="upiId"
                placeholder="yourname@paytm" 
                type="text"
                className="bank-input"
                value={formData.upiId}
                onChange={handleChange}
                required
              />
              {errors.upiId && (
                <span style={{ color: "#e53935", fontSize: "0.75rem", marginTop: "0.25rem", display: "block" }}>
                  {errors.upiId}
                </span>
              )}
            </div>
            
            <div className="bank-terms">
              By continuing you agree to the
              <a href="#" onClick={(e) => {
                e.preventDefault();
                console.log('Terms of Service clicked');
              }}> Terms of Service </a>
              and acknowledge you've read our
              <a href="#" onClick={(e) => {
                e.preventDefault();
                console.log('Privacy Policy clicked');
              }}> Privacy Policy</a>.
            </div>
            
            <button 
              type="submit"
              className="bank-register-btn" 
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                  Registering...
                </>
              ) : (
                <>
                  <i className="fas fa-camera" style={{ marginRight: '0.5rem' }}></i>
                  Complete Registration
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Bank;
